import React, { useEffect, useState } from "react";
import EmployeeManager from "../managers/EmployeeManager";
import TimesheetManager from "../managers/TimesheetManager";
import { useMsal } from "@azure/msal-react";
import { Day, Employee, EmployeeListPageProps } from "../interfaces/EmployeeInterface"; // Import Employee interfaces
import "./EmployeeListPage.css";
import Colors from '../styles/Colors.json';
import { useSiteContext } from "../SiteContext";

// Importing utility functions from DateAndTimeUtil.ts
import { formatTime, getGrandTotals, getGrandTotalTimes, getTotalHours, convertToLocalTime } from "../util/DateAndTimeUtil";
import { Page } from "../interfaces/PagesInterface";
import PageWrapper from "../components/PageWrapper";
import SearchBar from "../components/SearchBar";
import {TimesheetType, TimeEntryType} from "../interfaces/TimesheetInterface";
import {findIssues, getTotalAmountForWeek, getTotalTaskCountForWeek, hasSickClaimsWithZeroAmount} from "../util/TaskUtil";
import ActionButton from "../components/ActionButton";
import {printTimesheet} from "../util/PrintUtil";
import config from "../configLoader";
import DollarUtil from "../util/DollarUtil";

const EmployeeListPage: React.FC<EmployeeListPageProps> = ({ setError }) => {
  const { setLoading, setEmployee, type, payPeriodID, setTimesheetID, superID, superName, setPage, translator, superAltID, week, createPopup, payPeriods, userRole, userID } = useSiteContext();
  const [data, setData] = useState<Employee[] | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>(""); // State to manage the search term
  
  // Get the current pay period object
  const currentPayPeriod = payPeriods.find(pp => pp.PayPeriodID === payPeriodID);

  // Fetch employee data when the component mounts or when parameters change
  useEffect(() => {
    const fetchData = async () => {
      if (payPeriodID == null || type == null || superID == null) return;

      setLoading(true);

      try {
        let response;
        
        // Approval Access (WP8): the server scopes results from the MSAL session, so we
        // no longer compute/pass an adminUserEmployeeID (the "admin/payroll/hr see all"
        // opt-in is gone). The drill-down superID/managerEmployeeID stays and is validated
        // against the caller's scope server-side.
        if (type === TimesheetType.ManagerPending && superAltID) {
          if (superAltID.startsWith('manager_')) {
            const managerEmployeeID = parseInt(superAltID.replace('manager_', ''));
            response = await EmployeeManager.getRequiredManagerPendingEmployees(payPeriodID, managerEmployeeID);
          } else if (superAltID.startsWith('approvalGroup_')) {
            const approvalGroupID = parseInt(superAltID.replace('approvalGroup_', ''));
            response = await EmployeeManager.getRequiredApprovalGroupEmployees(payPeriodID, approvalGroupID);
          } else {
            response = await EmployeeManager.getEmployees(payPeriodID, type, superID, null);
          }
        } else {
          response = await EmployeeManager.getEmployees(payPeriodID, type, superID, null);
        }
        
        setData(response.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to fetch data. Please try again later.");
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [payPeriodID, type, superID, superAltID]);

  const handleClick = (employee: Employee) => {
    setTimesheetID(employee.timesheetID);
    setEmployee(employee);
    setPage(Page.TimeSheetPage);
  };

  const onPrint = async (employee: Employee) => {
      setLoading(true);
      const result = await printTimesheet(employee, payPeriodID, currentPayPeriod!);
      if(!result){
        createPopup("Timecard Print Failed!", "");
      } else { createPopup("Timecard Print Successful!", "Check downloads for the file."); }
      setLoading(false);
    }; 

  // Function to approve all employees
  const onApproveAll = async () => {
    if (!filteredEmployees || filteredEmployees.length === 0) {
      createPopup("No Employees", "No employees to approve.");
      return;
    }

    setLoading(true);
    let successCount = 0;
    let failureCount = 0;
    let skippedCount = 0;
    const errors: string[] = [];

    try {
      // Loop through all filtered employees and approve each one
      for (const employee of filteredEmployees) {
        if (hasSickClaimsWithZeroAmount(employee.days) && !config.hideSickClaimHourlyRate) {
          skippedCount++;
          continue;
        }
        try {
          const timesheetID = parseInt(employee.timesheetID!.toString(), 10);
          const adminEmployeeID = userID;
          
          // Approve the timesheet using the same logic as individual approval
          const result = await TimesheetManager.approveTimesheet(timesheetID, adminEmployeeID, type!);
          
          if (result) {
            successCount++;
            console.log(`Successfully approved timesheet for ${employee.name.first} ${employee.name.last}`);
          } else {
            failureCount++;
            errors.push(`${employee.name.first} ${employee.name.last}: Approval failed`);
          }
        } catch (error) {
          failureCount++;
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';
          errors.push(`${employee.name.first} ${employee.name.last}: ${errorMessage}`);
          console.error(`Error approving timesheet for ${employee.name.first} ${employee.name.last}:`, error);
        }
      }

      // Show results
      if (successCount > 0 && failureCount === 0) {
        createPopup("Approval Complete", `Successfully approved all ${successCount} timesheets.${skippedCount > 0 ? ` Skipped ${skippedCount} with sick claims.` : ''}`);
        // Refresh the data to show updated status (server-scoped from session — WP8)
        const response = await EmployeeManager.getEmployees(payPeriodID!, type!, superID!, null);
        setData(response.data);
      } else if (successCount > 0 && failureCount > 0) {
        createPopup("Partial Approval", `Approved ${successCount} timesheets. ${failureCount} failed.${skippedCount > 0 ? ` Skipped ${skippedCount} with sick claims.` : ''} Check console for details.`);
        console.error("Approval errors:", errors);
        // Refresh the data to show updated status (server-scoped from session — WP8)
        const response = await EmployeeManager.getEmployees(payPeriodID!, type!, superID!, null);
        setData(response.data);
      } else {
        createPopup("Approval Failed", `Failed to approve any timesheets.${skippedCount > 0 ? ` Skipped ${skippedCount} with sick claims.` : ''} Check console for details.`);
        console.error("All approval attempts failed:", errors);
      }

    } catch (error) {
      console.error("Error in bulk approval process:", error);
      createPopup("Approval Error", "An error occurred during the bulk approval process.");
    } finally {
      setLoading(false);
    }
  };

  // Filter supervisors based on the search term
  const filteredEmployees = data?.filter((employee) => {
    const searchTermLower = searchTerm.toLowerCase(); // Convert search term to lowercase for case-insensitive comparison

    // Check if the search term contains letters (name search)
    if (/[a-zA-Z]/.test(searchTerm)) {
      const fullName = `${employee.name.first} ${employee.name.last}`.toLowerCase();
      return fullName.includes(searchTermLower); // Filter by name
    }

    if(employee.altEmployeeID) { return employee.altEmployeeID.toString().includes(searchTerm); }
    else { return employee.employeeID.toString().includes(searchTerm); }

    // Otherwise, filter by altEmployeeID (number search)
    return employee.altEmployeeID?.toString().includes(searchTerm) ?? employee.employeeID?.toString().includes(searchTerm);
    
  }) || [];

  const selectedGrandTotals = (employees: Employee[]) => {

    let totalREG = 0;
    let totalOT = 0;
    let totalDT = 0;

    employees.forEach(employee => {

      const totals = getGrandTotals(employee.days);

      totalREG += totals.totalReg;
      totalOT += totals.totalOT;
      totalDT += totals.totalDT;
      
    });

    return {
      totalReg: formatTime(totalREG),
      totalOT: formatTime(totalOT),
      totalDT: formatTime(totalDT),
    };

  }

  const selectedTotalHours = (employees: Employee[]) => {

    let total = 0;

    employees.forEach(employee => {

      const totals = getTotalHours(employee.days);

      total += totals.totalMinutes;
      
    });

    return {
      total: formatTime(total)
    };

  }

  // Calculate the average hourly rate for the employee by taking the total amount for the week and dividing by the total hours for the week
  // Returns "N/A" if the total hours or  total amount is 0
  function get2020PieceMinutesForDay(day: Day): number {
    let pieceMinutes = 0;
    if (!day.hourlyProjects || day.hourlyProjects.length === 0) return pieceMinutes;
    for (const hp of day.hourlyProjects) {
      let projectRegMinutes = 0;
      hp.timeEntriesHourly?.forEach((entry: any) => {
        if (entry.type !== TimeEntryType.Regular) return;
        if (!entry.outDateTime) return;
        const localIn = convertToLocalTime(entry.inDateTime, entry.inDateTimeOffset ?? "0:0");
        const localOut = convertToLocalTime(entry.outDateTime, entry.outDateTimeOffset ?? "0:0");
        const minutes = Math.round((localOut.getTime() - localIn.getTime()) / 60000);
        if (minutes > 0) projectRegMinutes += minutes;
      });
      const hasAccount = hp.account && hp.account.accountCodeID !== 0 && hp.account.accountCodeID !== undefined && hp.account.accountCodeID !== null;
      if (!hasAccount) pieceMinutes += projectRegMinutes;
    }
    return pieceMinutes;
  }

  function getAvgHourlyRate(days: Day[]) {
    const totalPieceMinutes = days.reduce((acc: number, day: Day) => {
      const pieceMinutes = config.use2020PieceHourlyCalculation
        ? get2020PieceMinutesForDay(day)
        : (day.totalTaskMinutes ?? 0);
      return acc + pieceMinutes;
    }, 0);
    const totalAmount = getTotalAmountForWeek(days);

    if (totalPieceMinutes === 0 || totalAmount === 0) {
      return "N/A";
    }

    return DollarUtil.formatDollar(Number(totalAmount / (totalPieceMinutes / 60)));
  }

  return (
    <PageWrapper title={"Employee Timesheet List"} backArrow={true} backgroundColor={Colors.White}>

             <div className="employeeList-top-summary">
         <h4><strong>{superName}</strong></h4>
        
        {/* Approve All button positioned in top right */}
        {type === TimesheetType.SuperApproved && (userRole.payroll || userRole.admin) && (
          <div className="approve-all-button" onClick={(e) => e.stopPropagation()}>
            <ActionButton
              title="Approve All"
              isEnabled={true}
              onAction={onApproveAll}
            />
          </div>
        )}
        
        <div style={{
          flexDirection: 'row', 
          display: "flex", 
          gap: "20px",
          justifyContent: 'end', 
          width: '100%', 
          padding: '5px', 
          verticalAlign: 'bottom'
        }}>
          {type !== 'unsubmitted' ? (
            <div className="grand-total-container">
              <span>Regular: {selectedGrandTotals(filteredEmployees).totalReg}</span>
              <span>Overtime: {selectedGrandTotals(filteredEmployees).totalOT}</span>
              <span>Double Time: {selectedGrandTotals(filteredEmployees).totalDT}</span>
            </div>
          ) : (

            <div className="grand-total-container">
              <span>Total Hours: {selectedTotalHours(filteredEmployees).total}</span>
            </div>
            

          )}

          {/* Search bar */}
          <SearchBar
              placeholder={"Search Timesheets"}
              value={searchTerm}
              onChange={setSearchTerm}
              style={{width: '75%'}}
            />
        </div>
      </div>

      <div>
          {filteredEmployees.length > 0 ? (
            filteredEmployees
              .sort((a, b) => {
                const altA = parseInt(a.altEmployeeID || '0', 10) || 0;
                const altB = parseInt(b.altEmployeeID || '0', 10) || 0;
                return altA - altB;
              })
              .map((employee: Employee) => (
                <div
                  key={employee.altEmployeeID ?? employee.employeeID}
                  className={`employeeList-card ${
                    type === TimesheetType.SuperApproved && hasSickClaimsWithZeroAmount(employee.days)
                      ? 'red-background'
                      : ''
                  }`}
                  onClick={() => handleClick(employee)}
                >
                  <div className="employeeList-info-container">
                    <div className="employeeList-info">
                      <h4>{employee.name.last + " - " + employee.name.first + " " + (employee.altEmployeeID ?? '')}</h4>
                      {(type === TimesheetType.PayApproved) && (
                        <div className="approved-container">
                          Pay Approved
                        </div>
                      )}
                      {(type === TimesheetType.SuperApproved) && (
                        <div className="approved-container">
                          Supervisor Approved
                        </div>
                      )}
                      {type === TimesheetType.Submitted && (
                        <div className="submitted-container">
                          Submitted
                        </div>
                      )}
                    </div>

                    {type !== 'unsubmitted' ? (
                      <div className="summary-container">
                        <div className="week-info-container">
                          <span>Amount: {DollarUtil.formatDollar(Number(getTotalAmountForWeek(employee.days).toFixed(2)))}</span>
                          <span>Tasks: {getTotalTaskCountForWeek(employee.days)}</span>
                          {config.showAvgHourlyRate && (
                            <span>Avg Hourly Rate: {getAvgHourlyRate(employee.days)}</span>
                          )}
                        </div>
                        <div className="week-info-container">
                          <span>Regular: {getGrandTotalTimes(employee.days).totalReg}</span>
                          <span>Overtime: {getGrandTotalTimes(employee.days).totalOT}</span>
                          <span>Double Time: {getGrandTotalTimes(employee.days).totalDT}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="week-info-container">
                        <span><strong>Total Hours: </strong> {formatTime(getTotalHours(employee.days).totalMinutes)}</span>
                        <span><strong>Tasks: </strong> {getTotalTaskCountForWeek(employee.days)}</span>
                        <span><strong>Amount: </strong> {DollarUtil.formatDollar(Number(getTotalAmountForWeek(employee.days).toFixed(2)))}</span>
                        {config.showAvgHourlyRate && (
                          <span><strong>Avg Hourly Rate: </strong> {getAvgHourlyRate(employee.days)}</span>
                        )}
                      </div>

                    )}
                  </div>
                  {findIssues(employee.days) !== "" && type === TimesheetType.SuperApproved ? (
                      <div style={{width: "125px"}}>
                        <span><strong>{findIssues(employee.days)}</strong></span>
                      </div>
                    ) : (
                      <></>
                  )}
                  {type !== TimesheetType.Unsubmitted && (
                    <div style={{ display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
                      <ActionButton
                        title="Print"
                        isEnabled={true}
                        onAction={() => {
                          onPrint(employee);
                        }}
                      />
                    </div>
                  )}
                </div>
              ))
          ) : (
            <p className="no-data-message">{translator.translate("NoEmployeesFound")}</p>
          )}
        </div>
    </PageWrapper>
  );
};

export default EmployeeListPage;
